import React from 'react';
import { Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { getCurrentUser } from '../utils/auth';
import NotificationBell from './Notifications/NotificationBell';
import ActionCenterPill from './ActionCenter';
import PayFeesDropdown from './Fees/PayFeesDropdown';
import BranchSelector from './Common/BranchSelector';
import CommandPalette from './Common/CommandPalette';
import { usePageTitle } from '../contexts/PageTitleContext';
import { STUDENT_TOP_LEVEL, titleForPath } from '../utils/studentShell';

const ROLE_LABELS = {
  tenantadmin: 'Platform Admin',
  superadmin: 'Super Admin',
  admin: 'Admin',
  instructor: 'Instructor',
  student: 'Student',
};

const TopNav = ({ onMenuClick }) => {
  const user = getCurrentUser();
  const userInitials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'SA';
  const roleLabel = ROLE_LABELS[user?.usertype] || user?.usertype || 'User';
  const { pageTitle } = usePageTitle();
  const location = useLocation();
  const isStudent = user?.usertype === 'student';

  // Top-level student pages keep their header in the top nav; deeper/internal
  // pages render their own PageHeader (and back button) in the page body, so
  // the top nav exposes no header slot, fallback title, or back button there.
  const isStudentTopLevel = isStudent && STUDENT_TOP_LEVEL.has(location.pathname);

  return (
    <header className="top-nav-header">
      <div className={`top-nav-content${isStudent ? ' top-nav-content--student' : ''}`}>
        <div className="top-nav-left">
          <button className="nav-action-btn d-md-none" onClick={onMenuClick} aria-label="Open menu">
            <Menu size={18} />
          </button>
          {(pageTitle || (isStudentTopLevel && titleForPath(location.pathname))) && (
            <span className="top-nav-page-title d-md-none">
              {pageTitle || titleForPath(location.pathname)}
            </span>
          )}

          {!isStudent && (
            <div id="top-nav-portal-left" className="top-nav-portal-left d-none d-md-flex"></div>
          )}

          {isStudentTopLevel && (
            <div className="top-nav-student-left d-none d-md-flex">
              {/* Page header (icon + title + description) is portaled here by
                  PageHeader, matching the admin/super-admin top-bar pattern.
                  Only top-level pages expose this slot; deeper pages render
                  their PageHeader (and its back button) in the page body. */}
              <div id="top-nav-portal-left" className="top-nav-portal-left"></div>
              {/* Fallback for top-level pages that don't render a PageHeader
                  (e.g. the dashboard): an empty pageTitle means nothing was
                  portaled into the slot above. */}
              {!pageTitle && titleForPath(location.pathname) && (
                <h1 className="top-nav-student-title">{titleForPath(location.pathname)}</h1>
              )}
            </div>
          )}
        </div>

        {isStudent && <CommandPalette />}

        <div className="top-nav-right">
          <div id="top-nav-portal" className="top-nav-portal"></div>
          <div className={`branch-selector-slot${location.pathname === '/dashboard' ? '' : ' branch-selector-slot--hide-mobile'}`}>
            <BranchSelector />
          </div>
          {isStudent && (
            <div className="d-none d-md-block">
              <PayFeesDropdown />
            </div>
          )}
          {/* What this institute should do next — setup steps, work sitting
              unattended, and paid-for features never opened. Deliberately in
              the shell rather than on the dashboard: a counsellor who lives in
              /enquiries never opens a chart, and the guidance has to reach
              them too. It renders nothing for students and instructors (the
              endpoint is admin-only) and nothing when there is nothing to say.

              ⚠️ Placed BEFORE the bell, not after. They are different promises —
              the bell clears when read, this clears when the work is done — and
              the reading order puts the standing list first. */}
          <ActionCenterPill />
          <NotificationBell />
          <div className="user-profile-section d-none d-md-flex">
            <div className="user-avatar">{userInitials}</div>
            <div className="user-details">
              <div className="user-name">{user?.name || 'User'}</div>
              <div className="user-role">{roleLabel}</div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopNav;
